import "@/styles/szadmin.css";
import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Eye, EyeOff, Lock } from "lucide-react";

const DISPLAY = '"Poppins", system-ui, sans-serif';
const BODY = '"Work Sans", system-ui, -apple-system, sans-serif';

const AdminLoginNew = ({ onAdminLogin }) => {
  const navigate = useNavigate();
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/login`,
        { email: credentials.email, password: credentials.password }
      );

      if (response.data.success) {
        localStorage.setItem("shootzu_admin", JSON.stringify(response.data.admin_user));
        localStorage.setItem("shootzu_admin_token", response.data.token);
        if (response.data.refresh_token) {
          localStorage.setItem("shootzu_admin_refresh_token", response.data.refresh_token);
        }
        if (response.data.admin_user?.email) {
          localStorage.setItem("shootzu_admin_email", response.data.admin_user.email);
        }
        toast.success("Admin access granted");
        onAdminLogin();
        navigate("/szadmin/dashboard");
      }
    } catch (error) {
      console.error("Admin login failed:", error);
      toast.error(error.response?.data?.detail || "Invalid admin credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="sz-root"
      style={{
        minHeight: "100vh",
        display: "flex",
        background:
          "linear-gradient(157deg,#5D2E84 0%,#3A1D57 30%,#1A1038 62%,#0A0E1A 100%)",
        backgroundAttachment: "fixed",
        fontFamily: BODY,
      }}
    >
      <style>{`
        @keyframes szl-spin { to { transform: rotate(360deg); } }
        @keyframes szl-bloom { 0%,100% { transform: translate(0,0) scale(1); } 50% { transform: translate(40px,30px) scale(1.12); } }
        .szl-input::placeholder { color: rgba(255,255,255,.35); }
        .szl-input:focus { border-color: rgba(40,192,208,.6) !important; box-shadow: 0 0 0 3px rgba(40,192,208,.12); }
        .szl-mobile-logo { display: none; }
        @media (max-width: 880px) {
          .szl-brand { display: none !important; }
          .szl-mobile-logo { display: flex !important; }
          .szl-form-logo { display: none !important; }
          .szl-head { text-align: center !important; justify-content: center !important; }
        }
        @media (prefers-reduced-motion: reduce) {
          .szl-bloom, .szl-watermark { animation: none !important; }
        }
      `}</style>

      {/* Brand panel (desktop) */}
      <div
        className="szl-brand"
        style={{
          flex: "1 1 46%",
          position: "relative",
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          padding: "48px 52px",
          borderRight: "1px solid rgba(255,255,255,.08)",
        }}
      >
        <div
          className="szl-bloom"
          style={{
            position: "absolute",
            left: "-120px",
            top: "-100px",
            width: "520px",
            height: "520px",
            borderRadius: "999px",
            background: "radial-gradient(circle,rgba(40,192,208,.22),transparent 65%)",
            animation: "szl-bloom 26s ease-in-out infinite",
          }}
        />
        <img
          className="szl-watermark"
          src="/assets/szadmin/symbol-white.svg"
          alt=""
          aria-hidden="true"
          style={{
            position: "absolute",
            right: "-110px",
            bottom: "-110px",
            width: "420px",
            height: "420px",
            opacity: 0.06,
            animation: "szl-spin 90s linear infinite",
          }}
        />

        {/* Top: wordmark + ADMIN pill */}
        <div style={{ position: "relative", zIndex: 1, display: "flex", alignItems: "center", gap: "14px" }}>
          <img
            src="/assets/szadmin/horizontal-white.png"
            alt="Shootzu"
            style={{ height: "38px", width: "auto", display: "block" }}
          />
          <span
            style={{
              fontFamily: DISPLAY,
              fontWeight: 700,
              fontSize: "11px",
              letterSpacing: "2px",
              color: "#28C0D0",
              background: "rgba(40,192,208,.12)",
              border: "1px solid rgba(40,192,208,.3)",
              padding: "5px 11px",
              borderRadius: "999px",
            }}
          >
            ADMIN
          </span>
        </div>

        {/* Middle: headline + stats */}
        <div style={{ position: "relative", zIndex: 1 }}>
          <div
            style={{
              fontFamily: DISPLAY,
              fontWeight: 800,
              fontSize: "42px",
              color: "#fff",
              letterSpacing: "-1.4px",
              lineHeight: 1.12,
            }}
          >
            Run the network<br />
            behind{" "}
            <span
              style={{
                background: "linear-gradient(110deg,#67C6B4,#28C0D0 45%,#C98AD4)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              the shoots.
            </span>
          </div>
          <div
            style={{
              fontSize: "15px",
              color: "rgba(255,255,255,.65)",
              lineHeight: 1.6,
              marginTop: "14px",
              maxWidth: "400px",
            }}
          >
            Approvals, payments, messaging and reports — the Shootzu operations console.
          </div>
          <div style={{ display: "flex", gap: "26px", marginTop: "26px" }}>
            {[
              ["Vetted", "every member"],
              ["On time", "every payment"],
              ["Audited", "every action"],
            ].map(([num, label]) => (
              <div key={num}>
                <div style={{ fontFamily: DISPLAY, fontWeight: 800, fontSize: "20px", color: "#fff" }}>
                  {num}
                </div>
                <div style={{ fontSize: "12px", color: "rgba(255,255,255,.5)" }}>{label}</div>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom */}
        <div style={{ position: "relative", zIndex: 1, fontSize: "12px", color: "rgba(255,255,255,.4)" }}>
          Internal console · Turbo Productions, Melbourne
        </div>
      </div>

      {/* Form panel */}
      <div
        style={{
          flex: "1 1 54%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          padding: "40px 24px",
        }}
      >
        <div className="sz-fade" style={{ width: "100%", maxWidth: "404px" }}>
          {/* Mobile logo (≤880px) */}
          <div className="szl-mobile-logo" style={{ justifyContent: "center", marginBottom: "30px" }}>
            <img
              src="/assets/szadmin/horizontal-white.png"
              alt="Shootzu"
              style={{ height: "52px", width: "auto", display: "block" }}
            />
          </div>

          {/* In-form logo (desktop) */}
          <div className="szl-form-logo" style={{ marginBottom: "26px" }}>
            <img
              src="/assets/szadmin/horizontal-white.png"
              alt="Shootzu"
              style={{ height: "44px", width: "auto", display: "block" }}
            />
          </div>

          {/* Heading */}
          <div className="szl-head" style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontFamily: DISPLAY,
                fontWeight: 800,
                fontSize: "28px",
                color: "#fff",
                letterSpacing: "-.6px",
              }}
            >
              Admin sign in
            </span>
            <span
              style={{
                fontFamily: DISPLAY,
                fontWeight: 700,
                fontSize: "10px",
                letterSpacing: "1.6px",
                color: "#28C0D0",
                background: "rgba(40,192,208,.12)",
                border: "1px solid rgba(40,192,208,.3)",
                padding: "4px 9px",
                borderRadius: "999px",
                flex: "0 0 auto",
              }}
            >
              SZADMIN
            </span>
          </div>
          <div
            className="szl-head"
            style={{ fontSize: "14px", color: "rgba(255,255,255,.6)", marginTop: "6px", marginBottom: "24px" }}
          >
            Restricted access — authorised staff only.
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "13px" }}>
            <div>
              <div style={{ fontSize: "11.5px", color: "rgba(255,255,255,.5)", marginBottom: "6px" }}>
                Email
              </div>
              <input
                type="email"
                className="szl-input"
                value={credentials.email}
                onChange={(e) => setCredentials((p) => ({ ...p, email: e.target.value }))}
                placeholder="you@shootzu.com"
                data-testid="admin-email-input"
                required
                style={inputStyle}
              />
            </div>

            <div>
              <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "6px" }}>
                <span style={{ fontSize: "11.5px", color: "rgba(255,255,255,.5)" }}>Password</span>
                <Link
                  to="/szadmin/forgot-password"
                  style={{ fontSize: "11.5px", fontWeight: 600, color: "#28C0D0", textDecoration: "none" }}
                >
                  Forgot?
                </Link>
              </div>
              <div style={{ position: "relative" }}>
                <input
                  type={showPassword ? "text" : "password"}
                  className="szl-input"
                  value={credentials.password}
                  onChange={(e) => setCredentials((p) => ({ ...p, password: e.target.value }))}
                  placeholder="••••••••••"
                  data-testid="admin-password-input"
                  required
                  style={{ ...inputStyle, paddingRight: "44px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((s) => !s)}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  style={{
                    position: "absolute",
                    right: "8px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    width: "30px",
                    height: "30px",
                    borderRadius: "8px",
                    background: "transparent",
                    border: "none",
                    color: "rgba(255,255,255,.45)",
                    cursor: "pointer",
                  }}
                >
                  {showPassword ? <EyeOff size={17} /> : <Eye size={17} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="admin-login-button"
              style={{
                width: "100%",
                padding: "14px",
                borderRadius: "12px",
                background: "#28C0D0",
                border: "none",
                color: "#0C1120",
                fontFamily: BODY,
                fontWeight: 700,
                fontSize: "14.5px",
                cursor: loading ? "default" : "pointer",
                opacity: loading ? 0.75 : 1,
                boxShadow: "0 12px 28px -10px rgba(40,192,208,.6)",
                marginTop: "4px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              {loading ? (
                <>
                  <span
                    style={{
                      display: "inline-block",
                      width: "16px",
                      height: "16px",
                      borderRadius: "999px",
                      border: "2px solid rgba(12,17,32,.35)",
                      borderTopColor: "#0C1120",
                      animation: "szl-spin 0.7s linear infinite",
                    }}
                  />
                  Authenticating…
                </>
              ) : (
                "Sign in to console"
              )}
            </button>
          </form>

          {/* Audit notice */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: "9px",
              marginTop: "20px",
              padding: "12px 14px",
              border: "1px solid rgba(255,255,255,.1)",
              borderRadius: "12px",
              background: "rgba(255,255,255,.03)",
            }}
          >
            <Lock size={15} color="#FCD34D" strokeWidth={2} style={{ flex: "0 0 auto" }} />
            <span style={{ fontSize: "12px", color: "rgba(255,255,255,.55)", lineHeight: 1.5 }}>
              Sessions are logged and audited. Two-factor verification may be required after sign in.
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const inputStyle = {
  width: "100%",
  boxSizing: "border-box",
  padding: "13px 14px",
  border: "1px solid rgba(255,255,255,.14)",
  borderRadius: "12px",
  background: "rgba(255,255,255,.04)",
  color: "#fff",
  fontFamily: BODY,
  fontSize: "14px",
  outline: "none",
};

export default AdminLoginNew;
